import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/client';

interface FormErrors {
    passcode?: string;
    general?: string;
}

interface SessionDetails {
    sessionId: string;
    sessionName: string;
    hostName: string;
    status: string;
}

export default function JoinSession() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [passcode, setPasscode] = useState('');
    const [errors, setErrors] = useState<FormErrors>({});
    const [isLoading, setIsLoading] = useState(false);
    const [isValidatingToken, setIsValidatingToken] = useState(false);

    /**
     * Validate invitation token and join session
     */
    const validateInvitationToken = useCallback(async (sessionId: string, token: string) => {
        setIsValidatingToken(true);
        setErrors({});

        try {
            const response = await apiClient.post<SessionDetails>(
                `/api/sessions/${sessionId}/validate`,
                { invitationToken: token }
            );

            // Navigate to listen page with session details
            navigate(`/listen/${response.sessionId}`, {
                state: {
                    sessionName: response.sessionName,
                    hostName: response.hostName,
                },
            });
        } catch (error) {
            console.error('Token validation error:', error);
            const errorMessage = error instanceof Error ? error.message : 'Invalid or expired invitation link';
            setErrors({ general: errorMessage });
        } finally {
            setIsValidatingToken(false);
        }
    }, [navigate]);

    /**
     * Handle invitation token from URL on component mount
     */
    useEffect(() => {
        const token = searchParams.get('token');
        const sessionId = searchParams.get('session');

        if (token && sessionId) {
            validateInvitationToken(sessionId, token);
        }
    }, [searchParams, validateInvitationToken]);

    /**
     * Validate passcode format (alphanumeric, exactly 6 characters)
     */
    const validatePasscode = (passcode: string): string | undefined => {
        if (!passcode) {
            return 'Passcode is required';
        }

        const trimmed = passcode.trim();
        if (trimmed.length !== 6) {
            return 'Passcode must be exactly 6 characters';
        }

        if (!/^[a-zA-Z0-9]+$/.test(trimmed)) {
            return 'Passcode must be alphanumeric';
        }

        return undefined;
    };

    /**
     * Handle passcode form submission
     */
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Clear previous errors
        setErrors({});

        // Validate passcode
        const passcodeError = validatePasscode(passcode);
        if (passcodeError) {
            setErrors({ passcode: passcodeError });
            return;
        }

        setIsLoading(true);

        try {
            // Find session by passcode (case-insensitive)
            const response = await apiClient.post<SessionDetails>(
                '/api/sessions/validate-passcode',
                { passcode: passcode.trim() }
            );

            // Navigate to listen page with session details
            navigate(`/listen/${response.sessionId}`, {
                state: {
                    sessionName: response.sessionName,
                    hostName: response.hostName,
                },
            });
        } catch (error) {
            console.error('Passcode validation error:', error);
            const errorMessage = error instanceof Error ? error.message : 'Invalid passcode';
            setErrors({ general: errorMessage });
        } finally {
            setIsLoading(false);
        }
    };

    // Show loading state while validating invitation token
    if (isValidatingToken) {
        return (
            <div className="join-session-container">
                <h1>Silent Disco - Listener</h1>
                <div className="loading-message">
                    <p>Validating invitation...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="join-session-container">
            <h1>Silent Disco - Listener</h1>
            <h2>Join Session</h2>

            <form onSubmit={handleSubmit} noValidate>
                {errors.general && (
                    <div className="error-message general-error" role="alert">
                        {errors.general}
                    </div>
                )}

                <div className="form-group">
                    <label htmlFor="passcode">Session Passcode</label>
                    <input
                        id="passcode"
                        type="text"
                        value={passcode}
                        onChange={(e) => {
                            // Convert to uppercase for better UX
                            setPasscode(e.target.value.toUpperCase());
                            if (errors.passcode) {
                                setErrors({ ...errors, passcode: undefined });
                            }
                        }}
                        onBlur={() => {
                            const error = validatePasscode(passcode);
                            if (error) {
                                setErrors({ ...errors, passcode: error });
                            }
                        }}
                        disabled={isLoading}
                        placeholder="ABC123"
                        maxLength={6}
                        aria-invalid={!!errors.passcode}
                        aria-describedby={errors.passcode ? 'passcode-error' : undefined}
                        autoComplete="off"
                    />
                    {errors.passcode && (
                        <span id="passcode-error" className="error-message" role="alert">
                            {errors.passcode}
                        </span>
                    )}
                    <span className="help-text">
                        Enter the 6-character passcode provided by the host
                    </span>
                </div>

                <button type="submit" disabled={isLoading}>
                    {isLoading ? 'Joining...' : 'Join Session'}
                </button>
            </form>

            <div className="info-section">
                <p>
                    <strong>Have an invitation link?</strong>
                </p>
                <p>
                    Click the link provided by the host to join automatically.
                </p>
            </div>
        </div>
    );
}
